class MaskedPhone extends HTMLElement {
  static TAG_NAME = "masked-phone";

  static ATTRIBUTE_DROPDOWN_OPEN = "dropdown-open";
  static ATTRIBUTE_DEFAULT_COUNTRY = "default-country";
  static ATTRIBUTE_INITIALIZED = "initialized";
  static ATTRIBUTE_INVALID = "invalid";

  static SESSION_STORAGE_KEY = "masked_phone_country"

  static observedAttributes = [
    MaskedPhone.ATTRIBUTE_DROPDOWN_OPEN,
    MaskedPhone.ATTRIBUTE_DEFAULT_COUNTRY,
  ];

  static _countriesData = null;

  static DEFAULT_COUNTRY = "RU";
  static EVENT_DATA_LOAD = "dataload";

  _country = null;
  _dropdown = null;
  _input = null;
  _form = null;
  _mask = null;
  _observer = null;
  _storageId = null;

  constructor() {
    super();

    this.attachShadow({ mode: "open" });

    this._initializeStorage();

    this._country = this.getSavedCountry();

    this._handleClick = this._handleClick.bind(this);
    this._handleFormReset = this._handleFormReset.bind(this);
    this._handleSlotChange = this._handleSlotChange.bind(this);
  }

  static getFlagImageSource(countryCode) {
    const extensionPath = BX.Extension.getSettings(
      "sotbit.b2c.masked_phone_data"
    ).get("extensionPath");

    if (!extensionPath) {
      return "";
    }

    return `${extensionPath}/flags/${countryCode}.svg`;
  }

  update() {
    this._render();

    this._mask?.updateOptions({
      mask: this.getCurrentCountryMask(),
    });
  }

  getCaption() {
    return this.shadowRoot.querySelector('[part="caption"]').textContent;
  }

  getCurrentCountryMask() {
    if (!MaskedPhone._countriesData) {
      return "";
    }

    return `${MaskedPhone._countriesData[this._country].code}${
      MaskedPhone._countriesData[this._country].mask
    }`;
  }

  getDefaultCountry() {
    return (
      this.getAttribute(MaskedPhone.ATTRIBUTE_DEFAULT_COUNTRY) ??
      MaskedPhone.DEFAULT_COUNTRY
    );
  }

  changeCountry(code) {
    if (MaskedPhone._countriesData?.[code]) {
      this._country = code;
    } else {
      this._country = this.getDefaultCountry();
    }

    this.saveCurrentCountry();
    this.update();
  }

  checkValidity() {
    if (!this._input || !window.IMask) {
      return true;
    }

    const masked = window.IMask.createMask({
      mask: this.getCurrentCountryMask()
    });

    masked.resolve(this._input.value);

    const { code, mask } = MaskedPhone._countriesData[this._country];
    const targetLength = `${code}${mask}`.replaceAll(
      /[\-+()1-9]*(?:\\0)*/g,
      ""
    ).length;

    return masked.unmaskedValue.length === targetLength;
  }

  getSavedCountry() {
    return window.sessionStorage.getItem(this._storageId);
  }

  saveCurrentCountry() {
    window.sessionStorage.setItem(this._storageId, this._country);
  }

  _createFlag(code, name) {
    return `
        <img part="flag" 
             loading="lazy"
             src="${MaskedPhone.getFlagImageSource(code)}" 
             alt="${BX.message(
               "SOTBIT_B2C_MASKED_PHONE_FLAG_IMAGE_ALT"
             ).replace("#COUNTRY#", name)}"  
             width="24" 
             height="24" 
             inert
        >
      `;
  }

  _createStyle() {
    return `
        <style>
          :host {
            position: relative;
            display: grid;
            grid-template-columns: 1fr;
            column-gap: 4px;
            row-gap: 4px;
          }
          
          :host([${MaskedPhone.ATTRIBUTE_INITIALIZED}]) {
            grid-template-columns: 64px 1fr;
          }

          [part="toggle"] {
            all: unset;
            display: flex;
            align-items: center;
            column-gap: 8px;
            height: 50px;
            padding: 0 7px;
            background-color: var(--white);
            border: 1px solid var(--secondary-500);
            border-radius: var(--border-radius-sm);
            outline: transparent solid 4px;
            cursor: pointer;
            transition: border-color var(--transition), outline-color var(--transition);
          }

          @media (hover: hover) {
            [part="toggle"]:hover {
              border-color: var(--primary-hover);
            }
          }

          [part="toggle"]:focus-visible,
          [part="toggle"]:active {
            border-color: var(--primary);
            outline-color: var(--primary-light);
          }

          [part="toggle-icon"] {
            width: 16px;
            height: 16px;
            background-image: url("data:image/svg+xml, %3Csvg viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3.14645 5.64645C3.34171 5.45118 3.65829 5.45118 3.85355 5.64645L8 9.79289L12.1464 5.64645C12.3417 5.45118 12.6583 5.45118 12.8536 5.64645C13.0488 5.84171 13.0488 6.15829 12.8536 6.35355L8.35355 10.8536C8.15829 11.0488 7.84171 11.0488 7.64645 10.8536L3.14645 6.35355C2.95118 6.15829 2.95118 5.84171 3.14645 5.64645Z' fill='%232C2C2C'/%3E%3C/svg%3E");
            transition: transform var(--transition);
          }

          :host([${MaskedPhone.ATTRIBUTE_DROPDOWN_OPEN}]) [part="toggle-icon"] {
            transform: rotate(180deg);
          }
          
          :host([${MaskedPhone.ATTRIBUTE_INVALID}]) [part="toggle"] {
            border-color: var(--error);          
          }
          
          :host([${MaskedPhone.ATTRIBUTE_INVALID}]) slot[name="input"]::slotted(input) {
            border-color: var(--error) !important;
          }
          
          :host([${MaskedPhone.ATTRIBUTE_INVALID}]) [part="toggle"]:focus-visible,
          :host([${MaskedPhone.ATTRIBUTE_INVALID}]) [part="toggle"]:active {
            outline-color: var(--error-light);          
          }
          
          :host([${MaskedPhone.ATTRIBUTE_INVALID}]) slot[name="input"]::slotted(input:focus-visible),
          :host([${MaskedPhone.ATTRIBUTE_INVALID}]) slot[name="input"]::slotted(input:active) {
            outline-color: var(--error-light) !important;
          }
        </style>
    `;
  }

  _createToggle() {
    if (!MaskedPhone._countriesData) {
      return "";
    }

    return `
      <button part="toggle">
        ${this._createFlag(
          this._country,
          MaskedPhone._countriesData[this._country].name
        )}
        <div part="toggle-icon"></div>
      </button>
    `;
  }

  _createDropdown() {
    const dropdown = document.createElement("masked-phone-dropdown");

    dropdown._maskedPhone = this;

    return dropdown;
  }

  _handleClick(event) {
    if (event.target.closest('[part="toggle"]')) {
      this.toggleAttribute(MaskedPhone.ATTRIBUTE_DROPDOWN_OPEN);
    }
  }

  _handleFormReset() {
    // when form reset event happens, input still has old value; that's why we wait for the value to update
    queueMicrotask(() => {
      if (this._mask) {
        this._mask.value = this._input.value;
      }
    });
  }

  _handleSlotChange() {
    const slot = this.shadowRoot.querySelector("slot");

    this._input = slot.assignedElements()[0];

    this._addInputEvents();
  }

  _selectCountry(code) {
    this.changeCountry(code);
    this.removeAttribute(MaskedPhone.ATTRIBUTE_DROPDOWN_OPEN);
  }

  _handleDropdownOpenAttributeChange(open) {
    if (open) {
      this._openDropdown();
    } else {
      this._closeDropdown();
    }
  }

  _render() {
    this.shadowRoot.innerHTML = `
        ${this._createStyle()}
        ${this._createToggle()}
        <slot name="input"></slot>
    `;
  }

  _openDropdown() {
    this._dropdown = this._createDropdown();

    document.body.appendChild(this._dropdown);

    this._dropdown._adjust();
  }

  _closeDropdown() {
    this._dropdown?.remove();
  }

  _loadData() {
    BX.ready(() => {
      BX.loadExt("sotbit.b2c.masked_phone_data").then(() => {
        this.dispatchEvent(
          new CustomEvent(MaskedPhone.EVENT_DATA_LOAD, {
            bubbles: true,
            composed: true,
          })
        );
      });
    });
  }

  _initialize() {
    if (!MaskedPhone._countriesData) {
      MaskedPhone._countriesData = JSON.parse(
        BX.Extension.getSettings("sotbit.b2c.masked_phone_data").get("data")
      );
    }

    this.update();
    this._createMask();
    this.setAttribute(MaskedPhone.ATTRIBUTE_INITIALIZED, '');
  }

  _initializeIntersectionObserver() {
    this.observer = new IntersectionObserver((entries, observer) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this._loadData();
          observer.disconnect();
        }
      }
    });

    this.observer.observe(this);
  }

  _createMask() {
    if (!this._mask) {
      this._mask = window.IMask?.(this._input, {
        mask: this.getCurrentCountryMask(),
        lazy: false,
      });
    }
  }

  _addInputEvents() {
    if (!this._input) {
      return;
    }

    if (!this._form && this._input.form) {
      this._form = this._input.form;
    }

    if (this._form) {
      this._form.removeEventListener("reset", this._handleFormReset);
      this._form.addEventListener("reset", this._handleFormReset);
    }
  }

  _initializeStorage() {
    this._storageId = this._generateStorageId();
  }

  _generateStorageId() {
    return MaskedPhone.SESSION_STORAGE_KEY;
  }

  connectedCallback() {
    // in case "default-country" attribute is not set
    if (!this._country) {
      this._country = MaskedPhone.DEFAULT_COUNTRY;
    }

    this.shadowRoot.addEventListener("click", this._handleClick);
    this.shadowRoot.addEventListener("slotchange", this._handleSlotChange);

    this.addEventListener(
      MaskedPhone.EVENT_DATA_LOAD,
      this._initialize.bind(this),
      { once: true }
    );

    this._initializeIntersectionObserver();

    this.update();
  }

  disconnectedCallback() {
    this.removeAttribute(MaskedPhone.ATTRIBUTE_DROPDOWN_OPEN);

    this._form?.removeEventListener("reset", this._handleFormReset);

    this.observer?.disconnect();
    this.observer = null;

    this._mask?.destroy();
    this._mask = null;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case MaskedPhone.ATTRIBUTE_DROPDOWN_OPEN:
        this._handleDropdownOpenAttributeChange(newValue !== null);
        break;
      case MaskedPhone.ATTRIBUTE_DEFAULT_COUNTRY:
        if (!this._country) {
          this._country = newValue;
          this.update();
        }
        break;
    }
  }
}

/**
 * @internal
 */
class MaskedPhoneDropdown extends HTMLElement {
  static TAG_NAME = "masked-phone-dropdown";

  _maskedPhone = null;

  constructor() {
    super();

    this.attachShadow({ mode: "open" });

    this._handleClick = this._handleClick.bind(this);
    this._handleClickOutside = this._handleClickOutside.bind(this);
    this._handleFocusOut = this._handleFocusOut.bind(this);
    this._handleInputFocus = this._handleInputFocus.bind(this);
    this._handleScroll = this._handleScroll.bind(this)
    this._handleResize = this._handleResize.bind(this)
  }

  _adjust() {
    if (!this._maskedPhone) {
      return;
    }

    const { top, bottom, left } = this._maskedPhone.getBoundingClientRect();
    const dropdownHeight = this.offsetHeight;

    if (dropdownHeight + bottom > window.innerHeight) {
      this.style.top = `${top - dropdownHeight}px`;
    } else {
      this.style.top = `${bottom}px`;
    }

    this.style.left = `${left}px`;
  }

  _createStyle() {
    return `
        <style>
          :host {
            position: fixed;
            padding: 8px 10px 8px 8px;
            background-color: var(--white);
            border-radius: var(--border-radius);
            box-shadow: var(--shadow);
            z-index: calc(infinity);
          }

          [part="list"] {
            max-height: 200px;
            overflow: auto;
          }
          
          [part="list"]::-webkit-scrollbar {
            width: 2px;
            height: 2px;
          }
        
          [part="list"]::-webkit-scrollbar-track {
            background-color: var(--secondary-200);
          }
        
          [part="list"]::-webkit-scrollbar-thumb {
            background-color: var(--primary);
            border-radius: 2px;
            transition: background-color var(--transition);
          }
        
          [part="list"]::-webkit-scrollbar-thumb:hover {
            background-color: var(--primary-hover);
          }

          [part="list-item"] {
            all: unset;
            box-sizing: border-box;
            display: flex;
            align-items: center;
            column-gap: 8px;
            width: 100%;
            padding: 8px 6px;
            color: var(--dark);
            line-height: 1.4;
            cursor: pointer;
            transition: color var(--transition);
          }

          @media (hover: hover) {
            [part="list-item"]:hover {
              color: var(--primary-hover);
            }
          }

          [part="list-item"]:focus-visible,
          [part="list-item"]:active {
            color: var(--primary);
          }
        </style>
      `;
  }

  _createContent() {
    if (!MaskedPhone._countriesData || !this._maskedPhone) {
      return "";
    }

    return `
        <div part="list">
          ${Object.entries(MaskedPhone._countriesData)
            .map((record) => {
              const [countryCode, data] = record;

              // backslash is used in mask pattern to escape zero (0) character, we strip it for the list view
              const phoneCode = data.code.replaceAll("\\", "");

              return `
                <button part="list-item" data-code="${countryCode}">
                  ${this._maskedPhone._createFlag(countryCode, data.name)}
                  <span part="name">${data.name} (${phoneCode})</span>
                </button>
              `;
            })
            .join("")}
        </div>
      `;
  }

  _handleClick(event) {
    const listItem = event.target.closest('[part="list-item"]');

    if (listItem) {
      this._maskedPhone?._selectCountry(listItem.dataset.code);
    }
  }

  _handleClickOutside(event) {
    if (!event.composedPath().includes(this)) {
      this._maskedPhone?.removeAttribute(MaskedPhone.ATTRIBUTE_DROPDOWN_OPEN);
    }
  }

  _handleInputFocus() {
    this._maskedPhone?.removeAttribute(MaskedPhone.ATTRIBUTE_DROPDOWN_OPEN);
  }

  _handleFocusOut(event) {
    if (event.relatedTarget !== this) {
      this._maskedPhone?.removeAttribute(MaskedPhone.ATTRIBUTE_DROPDOWN_OPEN);
    }
  }

  _handleScroll() {
    this._maskedPhone?.removeAttribute(MaskedPhone.ATTRIBUTE_DROPDOWN_OPEN);
  }

  _handleResize() {
    this._adjust();
  }

  _render() {
    this.shadowRoot.innerHTML = `
        ${this._createStyle()}
        ${this._createContent()}
      `;
  }

  connectedCallback() {
    if (!this._maskedPhone) {
      console.warn(
        `Masked phone instance is not set. Dropdown component is not intended to be used independently of MaskedPhone component. To use it properly, toggle "${MaskedPhone.ATTRIBUTE_DROPDOWN_OPEN}" attribute of <masked-phone> element.`
      );
    }

    this._render();

    this.shadowRoot.addEventListener("click", this._handleClick);

    if (this._maskedPhone) {
      this._maskedPhone._input.addEventListener(
        "focus",
        this._handleInputFocus
      );
      this._maskedPhone.shadowRoot.addEventListener(
        "focusout",
        this._handleFocusOut
      );
    }

    window.addEventListener("scroll", this._handleScroll, { passive: true });
    window.addEventListener("resize", this._handleResize);

    // we need to defer adding click listener because it gets triggerred immediately after opening dropdown and closes it
    setTimeout(() => {
      window.addEventListener("click", this._handleClickOutside);
    });
  }

  disconnectedCallback() {
    if (this._maskedPhone) {
      this._maskedPhone._input.removeEventListener(
        "focus",
        this._handleInputFocus
      );
      this._maskedPhone.shadowRoot.removeEventListener(
        "focusout",
        this._handleFocusOut
      );
    }

    window.removeEventListener("click", this._handleClickOutside);
    window.removeEventListener("scroll", this._handleScroll);
    window.removeEventListener("resize", this._handleResize);
  }
}

if (!customElements.get(MaskedPhone.TAG_NAME)) {
  customElements.define(MaskedPhone.TAG_NAME, MaskedPhone);
}

if (!customElements.get(MaskedPhoneDropdown.TAG_NAME)) {
  customElements.define(MaskedPhoneDropdown.TAG_NAME, MaskedPhoneDropdown);
}
